// The active organization: its public profile — the name and contacts every
// tournament shows, which admins edit — and its admins, for admins

import { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import Spinner from './Spinner';
import Input from './Input';
import TextArea from './TextArea';
import Avatar from './Avatar';
import InviteButton from './InviteButton';
import { useOrgProfile, useOrgUsers } from '../utils/hooks';
import { UsersRoles } from '../utils/enums';
import { organizationUpdateProfile } from '../utils/firebase';
import './OrganizationModalBody.scss';

// The backend's limit, so the field stops where the call would refuse
const contactsMax = 2000;

function ProfileForm({ name, contacts }) {
  const [spinner, setSpinner] = useState(false);
  // Re-seeded by the snapshot that echoes a save, which carries what was typed
  const initialValues = useMemo(() => ({ name, contacts }), [name, contacts]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      name: Yup.string().trim().max(100, 'Too long').required('Required'),
      contacts: Yup.string().max(contactsMax, 'Too long'),
    }),
    onSubmit: async (values) => {
      setSpinner(true);
      try {
        await organizationUpdateProfile(values);
        toast.success('Organization saved');
      } catch (error) {
        toast.error(error?.message ?? 'Failed to save the organization');
      } finally {
        setSpinner(false);
      }
    },
  });

  return (
    <form className='profile-form' onSubmit={formik.handleSubmit}>
      <Input
        name='name'
        placeholder='Name'
        {...formik.getFieldProps('name')}
        error={formik.touched.name && formik.errors.name}
      />
      <TextArea
        name='contacts'
        placeholder='Contacts: who to call, phones, e-mail'
        rows={4}
        maxLength={contactsMax}
        {...formik.getFieldProps('contacts')}
        error={formik.touched.contacts && formik.errors.contacts}
      />
      <p className='hint font-14'>
        Shown on every tournament page and in the printouts.
      </p>
      <button type='submit' disabled={spinner || !formik.dirty}>
        Save
      </button>
      <Spinner show={spinner} />
    </form>
  );
}

function AdminsList() {
  const { isModalOpen } = useModal();
  const { users: admins, isLoaded } = useOrgUsers(
    isModalOpen ? UsersRoles.ADMIN : null,
  );
  const sorted = useMemo(
    () =>
      admins.toSorted((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '', 'en', {
          sensitivity: 'base',
        }),
      ),
    [admins],
  );

  return (
    <div className='admins'>
      <div className='controls'>
        <h5>Admins</h5>
        <InviteButton
          tokenCreatePayload={{ role: UsersRoles.ADMIN }}
          invited='admins'
        >
          Invite admins
        </InviteButton>
      </div>
      {!isLoaded && <Spinner show inline />}
      <ul>
        {sorted.map((admin) => (
          <li key={admin.id}>
            <Avatar user={admin} size={32} />
            <span className='font-medium'>{admin.name}</span>
            <span className='email'>{admin.email}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function OrganizationModalBody() {
  const { activeOrg, orgsById, userIsAdmin } = useFirebase();
  // The body outlives the closed modal; its listeners must not
  const { isModalOpen } = useModal();
  const { profile, isLoaded } = useOrgProfile(isModalOpen ? activeOrg : null);

  // A profile nobody has saved yet still has the members' name for it
  const name = profile?.name || orgsById.get(activeOrg)?.name || '';
  const contacts = profile?.contacts ?? '';

  return (
    <div className='organization-modal'>
      {!isLoaded ? (
        <Spinner show inline />
      ) : userIsAdmin ? (
        <ProfileForm name={name} contacts={contacts} />
      ) : (
        <div className='profile'>
          <h5>{name}</h5>
          <p className='contacts'>{contacts || 'No contacts yet'}</p>
        </div>
      )}
      {userIsAdmin && <AdminsList />}
    </div>
  );
}
